import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { useLocation } from 'react-router'
import authProvider from '../../core/AuthenticationProvider'
import { Element } from '../common/html/element'
import { Image } from '../common/image'
import { getInnerWidth } from '../helpers/device'
import { responsive } from '../utils/layout'
import './Chatbot.scss'
import chatbotIcon from './chatbot.svg'
import ContentChat from './component/ContentChat'
import FooterChatbot from './component/FooterChatbot'
import HeaderChatbot from './component/HeaderChatbot'
import HistoryChatbot from './component/HistoryChatbot'
import PendingResponseBubble from './component/PendingResponseBubble'
import QuestionSuggest from './component/QuestionSuggest'
import TextActionBubble from './component/TextActionBubble'
import { STATUS_MESS, USER_TYPE_CODE } from './constants'
import SignalRService from './core/SignalRService'
import './lang/i18n'
import {
  addMessageInTopic,
  changeShowHistoryList,
  changeTopicActive,
  changeTyping,
  changStatusMess,
  selectMessageTopic,
  selectStatusMess,
  selectTopicActive,
  updateBotMessage,
} from './store/slice'
import { getQuestion, getTopicMaxUpdatedDate } from './store/thunk'

// Chiều rộng mặc định khi mở fullscreen (px)
const DEFAULT_FULLSCREEN_WIDTH = 480
// Giới hạn resize
const MIN_FULLSCREEN_WIDTH = 320
const MAX_FULLSCREEN_WIDTH_RATIO = 0.8 // 80vw

const Chatbot = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const params = new Proxy(new URLSearchParams(location.search), {
    get: (searchParam, prop) => searchParam.get(prop),
  })
  const locale = useSelector((state) => state.i18n.locale)

  const messageTopic = useSelector(selectMessageTopic)
  const statusMess = useSelector(selectStatusMess)
  const topicActive = useSelector(selectTopicActive)

  // State hiển thị cục bộ (chỉ lưu tại màn hình)
  const [isOpen, setIsOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [fullscreenWidth, setFullscreenWidth] = useState(
    DEFAULT_FULLSCREEN_WIDTH,
  )

  const [question, setQuestion] = useState('')
  const [questionReply, setQuestionReply] = useState('')
  const [isChangeLayout, setIsChangeLayout] = useState(false)

  const popupRef = useRef(null)
  const chatbotBodyRef = useRef(null)
  const messageInputRef = useRef(null)
  const activeTimersRef = useRef([])
  const messagesScrollRef = useRef(null)
  const currentBotMessageIdRef = useRef(null)
  const userMessageTimestampRef = useRef(null)
  // Ref lưu chiều rộng hiện tại khi đang drag (tránh closure stale)
  const isDraggingRef = useRef(false)
  const dragStartXRef = useRef(0)
  const dragStartWidthRef = useRef(0)
  // Ref cho ghost line overlay khi resize
  const ghostLineRef = useRef(null)

  const toggleChatbot = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      dispatch(changeShowHistoryList(false))
      dispatch(
        changeTyping({
          messageId: null,
          done: true,
        }),
      )
    }
  }

  // Tự động reset trạng thái chatbot khi chuyển màn hình (URL thay đổi)
  useEffect(() => {
    setIsOpen(false)
    setIsFullscreen(false)
    setFullscreenWidth(DEFAULT_FULLSCREEN_WIDTH)
  }, [location.pathname])

  // Áp dụng CSS variable để đẩy nội dung chính sang trái
  const applyLayoutShift = useCallback((width) => {
    const mainEl = document.getElementById('SCROLL_LAYOUT_ELEMENT_ID')
    if (mainEl) {
      mainEl.style.transition = 'margin-right 0.3s ease'
      mainEl.style.marginRight = `${width}px`
    }
  }, [])

  // Xóa layout shift khi đóng fullscreen
  const clearLayoutShift = useCallback(() => {
    const mainEl = document.getElementById('SCROLL_LAYOUT_ELEMENT_ID')
    if (mainEl) {
      mainEl.style.transition = 'margin-right 0.3s ease'
      mainEl.style.marginRight = ''
    }
  }, [])

  // Bật chế độ fullscreen
  const enterFullscreen = useCallback(() => {
    setIsOpen(true)
    setIsFullscreen(true)
    applyLayoutShift(fullscreenWidth)
  }, [fullscreenWidth, applyLayoutShift])

  // Tắt chế độ fullscreen, quay về popup
  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false)
    clearLayoutShift()
  }, [clearLayoutShift])

  // Toggle fullscreen từ header
  const onToggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen()
    } else {
      enterFullscreen()
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen])

  // Cập nhật layout shift dựa trên trạng thái Mở/Đóng và Fullscreen
  useEffect(() => {
    if (isOpen && isFullscreen) {
      applyLayoutShift(fullscreenWidth)
    } else {
      clearLayoutShift()
    }
  }, [
    isOpen,
    isFullscreen,
    fullscreenWidth,
    applyLayoutShift,
    clearLayoutShift,
  ])

  // Cleanup layout shift và ghost line khi unmount
  useEffect(() => {
    return () => {
      clearLayoutShift()
      // Dọn ghost line nếu còn tồn tại khi component bị unmount giữa chừng
      if (ghostLineRef.current) {
        try {
          document.body.removeChild(ghostLineRef.current)
        } catch (_) {}
        ghostLineRef.current = null
      }
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [clearLayoutShift])

  // ===== Resize Handle Logic (Deferred — ghost line + rAF throttle) =====
  const onResizeMouseDown = useCallback(
    (e) => {
      e.preventDefault()
      isDraggingRef.current = true
      dragStartXRef.current = e.clientX
      dragStartWidthRef.current = fullscreenWidth

      // Width tạm thời trong khi kéo (chưa apply vào state)
      let pendingWidth = fullscreenWidth
      // rAF id để cancel frame cũ tránh queue tích lũy
      let rafId = null

      // Tạo ghost line overlay
      const ghostLine = document.createElement('div')
      ghostLine.style.cssText = [
        'position:fixed',
        'top:0',
        'bottom:0',
        'width:3px',
        'background:rgba(3,78,162,0.6)',
        'box-shadow:0 0 8px rgba(3,78,162,0.4)',
        'z-index:9999',
        'pointer-events:none',
        'will-change:left', // hint GPU compositor, tránh layout thrashing
      ].join(';')
      ghostLine.style.left = `${window.innerWidth - fullscreenWidth}px`
      document.body.appendChild(ghostLine)
      ghostLineRef.current = ghostLine

      // Cursor toàn trang khi đang kéo
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const onMouseMove = (moveEvent) => {
        if (!isDraggingRef.current) return

        // Tính width mới từ delta (dùng biến ngoài closure để luôn dùng giá trị mới nhất)
        const deltaX = dragStartXRef.current - moveEvent.clientX
        const maxWidth = window.innerWidth * MAX_FULLSCREEN_WIDTH_RATIO
        pendingWidth = Math.min(
          maxWidth,
          Math.max(MIN_FULLSCREEN_WIDTH, dragStartWidthRef.current + deltaX),
        )

        // Throttle DOM update bằng rAF — chỉ update 1 lần/frame (≤60fps)
        if (rafId !== null) cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => {
          if (ghostLineRef.current) {
            ghostLineRef.current.style.left = `${
              window.innerWidth - pendingWidth
            }px`
          }
          rafId = null
        })
      }

      const onMouseUp = () => {
        isDraggingRef.current = false

        // Hủy rAF đang pending nếu có
        if (rafId !== null) {
          cancelAnimationFrame(rafId)
          rafId = null
        }

        // Xóa ghost line
        if (ghostLineRef.current) {
          document.body.removeChild(ghostLineRef.current)
          ghostLineRef.current = null
        }

        // Khôi phục cursor
        document.body.style.cursor = ''
        document.body.style.userSelect = ''

        // Apply width mới sau khi thả chuột
        setFullscreenWidth(pendingWidth)

        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [fullscreenWidth],
  )

  useEffect(() => {
    authProvider.getUser().then((user) => {
      const userId = user?.profile?.user_id
      dispatch(
        getQuestion({
          UserId: userId,
          ProductId: 1, // 1: FiinGate
        }),
      )
    })
  }, [locale])

  // Cleanup any streaming timers on unmount
  useEffect(() => {
    dispatch(getTopicMaxUpdatedDate({}))
    return () => {
      activeTimersRef.current.forEach((t) => clearInterval(t))
      activeTimersRef.current = []
    }
  }, [])

  // Register callbacks once; connect lazily on send
  useEffect(() => {
    // Setup callbacks
    SignalRService.onAck((ack) => {
      dispatch(
        changeTopicActive({
          topicId: ack?.topicId,
          topicTitle: ack?.topicTitle,
        }),
      )
      dispatch(changStatusMess(STATUS_MESS.PENDING))
    })

    SignalRService.onReplyMsg((replyMsg) => {
      dispatch(
        changeTopicActive({
          topicId: replyMsg?.topicId,
          topicTitle: replyMsg?.topicTitle,
        }),
      )
      dispatch(changStatusMess(STATUS_MESS.PROCESSING))
      if (!currentBotMessageIdRef.current) {
        // Log response time
        if (userMessageTimestampRef.current) {
          const responseTime = Date.now() - userMessageTimestampRef.current
          console.log(
            `[Chatbot] Response time: ${(responseTime / 1000).toFixed(2)}s`,
          )
          console.log(
            `[Chatbot] User sent at: ${new Date(
              userMessageTimestampRef.current,
            ).toLocaleTimeString()}`,
          )
          console.log(
            `[Chatbot] Bot replied at: ${new Date().toLocaleTimeString()}`,
          )
          userMessageTimestampRef.current = null
        }
        const aiMessageId = Date.now()
        currentBotMessageIdRef.current = aiMessageId
        dispatch(
          changeTyping({
            messageId: aiMessageId,
            done: false,
          }),
        )
        dispatch(
          addMessageInTopic({
            messageId: aiMessageId,
            userTypeCode: USER_TYPE_CODE.BOT,
            content: replyMsg.msgText, // đoạn đầu tiên
          }),
        )
      } else {
        // Update message BOT đang stream bằng cách nối thêm text
        dispatch(
          updateBotMessage({
            messageId: currentBotMessageIdRef.current,
            content: replyMsg.msgText,
          }),
        )
      }
      // const aiMessageId = Date.now()
      // dispatch(
      //   addMessageInTopic({
      //     messageId: aiMessageId,
      //     userTypeCode: USER_TYPE_CODE.BOT,
      //     content: replyMsg.msgText,
      //   }),
      // )
    })

    SignalRService.onReplyCompleted((replyMsg) => {
      currentBotMessageIdRef.current = null
      dispatch(
        changeTopicActive({
          topicId: replyMsg?.topicId,
          topicTitle: replyMsg?.topicTitle,
        }),
      )
    })

    SignalRService.onError((error) => {
      console.error('SignalR Error:', error)
    })

    return () => {
      SignalRService.disconnect()
    }
  }, [])

  const sendUserMessage = async (userContent, askText) => {
    if (!userContent || !userContent.trim()) return

    const { isConnected } = SignalRService.getConnectionStatus()
    if (!isConnected) {
      const userInfo = await authProvider.getUser()
      const connected = await SignalRService.initializeConnection(
        userInfo?.profile?.user_id,
      )
      if (!connected) {
        const errorMessage = {
          messageId: Date.now() + 1,
          userTypeCode: 'system',
          content: I18n.t('chatbot.CONNECTION_ERROR'),
          isError: true,
        }
        dispatch(addMessageInTopic(errorMessage))
        return
      }
    }

    // Track timestamp when user sends message
    const timestamp = Date.now()
    userMessageTimestampRef.current = timestamp
    console.log(
      `[Chatbot] User sent message at: ${new Date(
        timestamp,
      ).toLocaleTimeString()}`,
    )

    const userMessage = {
      messageId: timestamp,
      userTypeCode: USER_TYPE_CODE.USER,
      content: userContent,
      ...(askText ? { ask: askText } : {}),
    }
    dispatch(addMessageInTopic(userMessage))

    // Set status to PENDING with a slight delay for better UX
    setTimeout(() => {
      dispatch(changStatusMess(STATUS_MESS.PENDING))
    }, 400)

    setQuestion('')
    setQuestionReply('')
    // Scroll to bottom only once right after sending
    setTimeout(() => {
      try {
        messagesScrollRef.current && messagesScrollRef.current.scrollToBottom()
      } catch (_) {}
    }, 0)

    // Gửi tin nhắn qua SignalR
    const messageData = {
      topicId: topicActive?.topicId,
      messageText: userContent,
      languageCode: 'vi',
      quotedMessageId: askText ? 2 : null,
      quoteText: askText || null,
      quoteUrl: null,
      productId: 1, // 1: FiinGate
      organirationId: params.organizationId
        ? Number(params.organizationId)
        : null,
    }
    const success = await SignalRService.sendUserMessage(messageData)
    if (success) {
    } else {
      // Nếu gửi thất bại, hiển thị tin nhắn lỗi
      const errorMessage = {
        messageId: Date.now() + 1,
        userTypeCode: USER_TYPE_CODE.SYSTEM,
        content: I18n.t('chatbot.SEND_MESSAGE_ERROR'),
        isError: true,
      }
      dispatch(addMessageInTopic(errorMessage))
      // Reset status on error
      dispatch(changStatusMess(STATUS_MESS.ACTIVE))
    }
  }

  useEffect(() => {
    setQuestionReply(null)
  }, [topicActive])

  useEffect(() => {
    setTimeout(() => {
      messagesScrollRef.current?.scrollToBottom()
    }, 100)
  }, [isOpen, topicActive])

  useEffect(() => {
    let resizeTimer = null

    const resize = () => {
      // Sử dụng debounce để tránh giật khi resize window liên tục
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        const innerWidth = getInnerWidth()
        if (innerWidth < responsive.PIVOT_WIDTH) {
          setIsChangeLayout(true)
        } else {
          setIsChangeLayout(false)
        }

        // Đảm bảo fullscreenWidth không vượt quá giới hạn màn hình
        setFullscreenWidth((prevWidth) => {
          const maxWidth = innerWidth * MAX_FULLSCREEN_WIDTH_RATIO
          if (prevWidth > maxWidth) {
            return maxWidth
          }
          return prevWidth
        })
      }, 100) // 100ms debounce
    }

    resize()
    window.addEventListener('resize', resize)

    return () => {
      window.removeEventListener('resize', resize)
      if (resizeTimer) clearTimeout(resizeTimer)
    }
  }, [])

  return (
    <div
      className={`chatbot-container${
        isOpen && isFullscreen ? ' chatbot-fullscreen-mode' : ''
      }`}
      style={isOpen && isFullscreen ? { width: fullscreenWidth } : undefined}
    >
      {/* Chatbot Icon Button — Chỉ hiện khi chatbot đóng */}
      {!isOpen && (
        <div className="chatbot-icon" onClick={toggleChatbot}>
          <Image
            src={chatbotIcon}
            className="chatbot-img"
            alt="logo"
            style={{ width: 36, height: 36 }}
          />
          <Element style={{ fontSize: 8 }} className="chatbot-text">
            <Translate value="chatbot.FIINGATE_ASSISTANT" />
          </Element>
        </div>
      )}

      {/* Chatbot Popup / Fullscreen Panel */}
      {isOpen && (
        <div
          className={`chatbot-popup${
            isFullscreen ? ' chatbot-popup--fullscreen' : ''
          }`}
          style={
            isFullscreen ? undefined : { height: isChangeLayout ? 500 : 660 }
          }
          ref={popupRef}
        >
          {/* Resize handle — chỉ hiển thị khi fullscreen */}
          {isFullscreen && (
            <div
              className="chatbot-resize-handle"
              onMouseDown={onResizeMouseDown}
              title="Kéo để thay đổi chiều rộng"
            />
          )}

          <div className="chat-main-area">
            <HeaderChatbot
              toggleChatbot={toggleChatbot}
              isFullscreen={isFullscreen}
              onToggleFullscreen={onToggleFullscreen}
            />
            <div className="chatbot-body" ref={chatbotBodyRef}>
              {messageTopic.length === 0 ? (
                <QuestionSuggest
                  sendUserMessage={sendUserMessage}
                  isChangeLayout={isChangeLayout}
                />
              ) : (
                <ContentChat ref={messagesScrollRef} />
              )}
              {statusMess === STATUS_MESS.PENDING && <PendingResponseBubble />}
            </div>
            <FooterChatbot
              question={question}
              setQuestion={setQuestion}
              questionReply={questionReply}
              setQuestionReply={setQuestionReply}
              sendUserMessage={sendUserMessage}
              messageInputRef={messageInputRef}
            />
          </div>
          <TextActionBubble
            isOpen={isOpen}
            setQuestionReply={setQuestionReply}
            messageInputRef={messageInputRef}
            chatbotBodyRef={chatbotBodyRef}
            popupRef={popupRef}
          />
          <HistoryChatbot />
        </div>
      )}
    </div>
  )
}

export default Chatbot
